import { useEffect, useRef, useState } from 'react';
import { listMasters } from '../../services/productMasterService';
import type { CpmBrandRecord } from '../../types/centralProductMaster.types';

interface CpmBrandFilterProps {
    valueId: string | null;
    valueLabel: string | null;
    onChange: (next: { id: string | null; label: string | null }) => void;
    className?: string;
}

export default function CpmBrandFilter({
    valueId,
    valueLabel,
    onChange,
    className = '',
}: CpmBrandFilterProps) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<CpmBrandRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchError, setSearchError] = useState<string | null>(null);
    const rootRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const onDoc = (e: MouseEvent) => {
            if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        const t = window.setTimeout(async () => {
            const trimmed = query.trim();
            if (trimmed.length < 3) {
                setResults([]);
                setLoading(false);
                setSearchError(null);
                return;
            }
            setLoading(true);
            setSearchError(null);
            try {
                const data = await listMasters({
                    level: 'brand',
                    search: trimmed,
                    page: 1,
                    limit: 25,
                    status: 'ACTIVE',
                });
                if (!cancelled) {
                    setResults((data.items as CpmBrandRecord[]) ?? []);
                    setSearchError(null);
                }
            } catch {
                if (!cancelled) {
                    setResults([]);
                    setSearchError("Couldn't search brands — try again");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, 300);
        return () => {
            cancelled = true;
            window.clearTimeout(t);
        };
    }, [query, open]);

    const hasValue = !!valueId;
    const inputText = open ? query : (valueLabel ?? '');
    const showClear = inputText.length > 0;

    const clearSelection = () => {
        onChange({ id: null, label: null });
        setQuery('');
        setResults([]);
        setSearchError(null);
    };

    return (
        <div
            ref={rootRef}
            className={[
                'relative flex flex-col flex-[1_1_200px] min-w-[180px] max-w-[240px]',
                className,
            ].join(' ')}
            style={{ gap: 6, width: 200 }}
        >
            <span
                className="uppercase text-[var(--cpm-text-secondary)]"
                style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.06em' }}
            >
                Brand
            </span>
            <div
                className={[
                    'rounded-[var(--cpm-radius-10)] border-[1.5px] bg-white flex items-center',
                    open || hasValue
                        ? 'border-[var(--cpm-brand-purple)] shadow-[var(--cpm-ring-focus)]'
                        : 'border-[var(--cpm-border-strong)]',
                ].join(' ')}
                style={{ height: 38, paddingLeft: 11, paddingRight: 11, gap: 8 }}
            >
                <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="var(--cpm-text-tertiary)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="shrink-0"
                >
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.3-4.3" />
                </svg>
                <input
                    value={inputText}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        if (!open) setOpen(true);
                    }}
                    onFocus={() => {
                        setOpen(true);
                        setQuery(valueLabel ?? '');
                    }}
                    placeholder="Search brand"
                    className={[
                        'flex-1 min-w-0 bg-transparent border-0 outline-none placeholder:text-[var(--cpm-text-secondary)]',
                        hasValue && !open
                            ? 'text-[var(--cpm-text-primary)]'
                            : 'text-[var(--cpm-text-primary)]',
                    ].join(' ')}
                    style={{
                        fontSize: 13,
                        fontWeight: hasValue && !open ? 800 : 700,
                    }}
                />
                {showClear ? (
                    <button
                        type="button"
                        aria-label="Clear brand"
                        onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            clearSelection();
                        }}
                        className="shrink-0 inline-flex items-center justify-center w-[18px] h-[18px] rounded-full bg-[var(--cpm-surface-skeleton)] text-[var(--cpm-text-secondary)]"
                    >
                        <svg
                            width="10"
                            height="10"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M18 6 6 18" />
                            <path d="m6 6 12 12" />
                        </svg>
                    </button>
                ) : null}
            </div>
            {open ? (
                <div className="absolute top-full left-0 right-0 mt-1.5 z-30 bg-white border border-[var(--cpm-border-default)] rounded-[var(--cpm-radius-10)] shadow-[var(--cpm-shadow-dropdown)] p-[5px] flex flex-col cpm-animate-pop-in max-h-[264px] overflow-y-auto min-w-[210px]">
                    {loading ? (
                        <div className="flex flex-col gap-[7px] px-2.5 py-1.5 pb-2.5">
                            <span className="cpm-sk h-[13px] w-[72%]" />
                            <span className="cpm-sk h-[13px] w-[54%]" />
                            <span className="cpm-sk h-[13px] w-[64%]" />
                        </div>
                    ) : searchError ? (
                        <div className="px-2.5 py-2 text-[13px] font-semibold text-[var(--cpm-shortfall-text)]">
                            {searchError}
                        </div>
                    ) : query.trim().length < 3 ? (
                        <div className="px-2.5 py-2 text-[13px] font-semibold text-[var(--cpm-text-secondary)]">
                            Type at least 3 characters
                        </div>
                    ) : results.length === 0 ? (
                        <div className="px-2.5 py-2 text-[13px] font-semibold text-[var(--cpm-text-secondary)]">
                            No matches
                        </div>
                    ) : (
                        results.map((row) => {
                            const active = valueId === row._id;
                            return (
                                <button
                                    key={row._id}
                                    type="button"
                                    onClick={() => {
                                        onChange({ id: row._id, label: row.name });
                                        setOpen(false);
                                        setQuery(row.name);
                                    }}
                                    className={[
                                        'flex items-center gap-2 px-2.5 py-2 rounded-[var(--cpm-radius-8)] text-[13px] text-left',
                                        active
                                            ? 'font-extrabold text-[var(--cpm-brand-purple)]'
                                            : 'font-bold text-[var(--cpm-text-primary)] hover:bg-[var(--cpm-surface-subtle)]',
                                    ].join(' ')}
                                >
                                    <span className="flex-1 truncate">{row.name}</span>
                                    {active ? (
                                        <svg
                                            width="13"
                                            height="13"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="var(--cpm-brand-purple)"
                                            strokeWidth="3"
                                        >
                                            <path
                                                d="M20 6 9 17l-5-5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    ) : null}
                                </button>
                            );
                        })
                    )}
                </div>
            ) : null}
        </div>
    );
}
